import { useState } from "react";
import { X } from "lucide-react";
import { EMOTIONS, moodColor, moodLabel } from "@/lib/emotions";
import { useRemanso } from "@/context/RemansoContext";

const Dot = ({ color, opacity = 1 }) => (
  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color, opacity }} aria-hidden="true" />
);

const NuanceRow = ({ emotion, moods, onToggle, onWhat }) => (
  <div className="mt-3 rounded-[8px] bg-superficie p-3" data-testid="nuance-row">
    <p className="text-xs text-meta mb-2 flex items-center gap-2">
      <span>De suave a intensa</span>
      <button type="button" className="text-acento-texto hover:underline underline-offset-2" onClick={onWhat} data-testid={`emotion-what-${emotion.id}`}>¿Qué es?</button>
    </p>
    <div className="flex flex-wrap gap-2">
      {emotion.nuances.map((n, i) => {
        const mood = `${emotion.id}:${n}`;
        return (
          <button key={mood} type="button" aria-pressed={moods.includes(mood)} onClick={() => onToggle(mood)}
            className={`chip ${moods.includes(mood) ? "active" : ""}`} data-testid={`nuance-${emotion.id}-${i}`}>
            <Dot color={emotion.color} opacity={0.45 + i * 0.275} /> {n}
          </button>
        );
      })}
    </div>
  </div>
);

export const EmotionWheel = ({ moods, onChange }) => {
  const [open, setOpen] = useState(null);
  const { openInfo } = useRemanso();
  const toggle = (mood) => onChange(moods.includes(mood) ? moods.filter((m) => m !== mood) : [...moods, mood]);
  const emotion = EMOTIONS.find((e) => e.id === open);
  return (
    <div className="mt-10 max-w-[60ch]" data-testid="emotion-wheel">
      <p className="text-[15px] font-medium text-tinta">¿Cómo llegas?</p>
      <p className="text-[13px] text-meta mt-1">Toca una emoción y elige el matiz que más se parezca. Solo tú las nombras.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {EMOTIONS.map((e) => (
          <button key={e.id} type="button" aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}
            className={`chip ${open === e.id ? "chip-open" : ""}`} data-testid={`emotion-${e.id}`}>
            <Dot color={e.color} /> {e.name}
          </button>
        ))}
      </div>
      <button type="button" className="mt-3 text-[13px] text-acento-texto hover:underline underline-offset-2" onClick={() => openInfo("guia", "guia-raro")}
        data-testid="emotion-guide-link">¿No sabes cómo nombrarlo?</button>
      {emotion && <NuanceRow emotion={emotion} moods={moods} onToggle={toggle} onWhat={() => openInfo("guia", `guia-${emotion.id}`)} />}
      {moods.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="selected-moods">
          {moods.map((m) => (
            <span key={m} className="inline-flex items-center gap-2 rounded-full bg-resaltado pl-3 pr-1 py-1 text-sm text-tinta" data-testid={`selected-mood-${m}`}>
              <Dot color={moodColor(m)} /> {moodLabel(m)}
              <button type="button" aria-label={`Quitar ${moodLabel(m)}`} onClick={() => toggle(m)} data-testid={`remove-mood-${m}`}
                className="h-6 w-6 rounded-full inline-flex items-center justify-center hover:bg-hoja transition-colors">
                <X size={14} strokeWidth={1.75} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
